// Horas de servicio de la bitácora: relojes como los de las apps ELD, pero por
// omisión SÓLO CALCULAN. En México no aplican las 11/14/70 h de la FMCSA y cada
// compañía opera distinto (hay quien trabaja de madrugada), así que un reloj
// sólo dice "restan" y se pinta ámbar/rojo cuando la compañía configuró ese
// límite (companies.hos_limits, PUT /company). Puro y con tests (hos.test.ts).
import { addDays } from "../quickReports.compute";
import type { DailyUnitSummary, Segment } from "../types";
import { fmtHM } from "./bitacora";

// Mismo contrato que el backend (functions/src/modules/companies/hosLimits.js):
// horas > 0 o null = sin límite.
export type HosLimits = {
  driving_day_h: number | null;
  duty_day_h: number | null;
  rest_min_h: number | null;
  driving_week_h: number | null;
};
export const HOS_LIMIT_KEYS = ["driving_day_h", "duty_day_h", "rest_min_h", "driving_week_h"] as const;
export const NO_HOS_LIMITS: HosLimits = { driving_day_h: null, duty_day_h: null, rest_min_h: null, driving_week_h: null };
// Sólo de ejemplo para el botón del modal; nunca es el comportamiento normal.
export const FMCSA_EXAMPLE: HosLimits = { driving_day_h: 11, duty_day_h: 14, rest_min_h: 10, driving_week_h: 70 };
export const HOS_WARN_AT = 0.8;

// Lo que venga en el documento de la compañía; cualquier cosa rara = sin límite.
export const readHosLimits = (raw: unknown): HosLimits => {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const out = { ...NO_HOS_LIMITS };
  for (const k of HOS_LIMIT_KEYS) {
    const v = src[k];
    out[k] = typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
  }
  return out;
};
export const hasHosLimits = (l: HosLimits) => HOS_LIMIT_KEYS.some((k) => l[k] != null);

// calc: sólo se mide (color del estado) · ok/warn/over: contra un máximo
// (ámbar desde 80 %, rojo al pasar) · pending/met: contra un mínimo (descanso).
export type HosLevel = "calc" | "ok" | "warn" | "over" | "pending" | "met";

export const hosLevel = (usedS: number, limitS: number, warnAt = HOS_WARN_AT): HosLevel =>
  usedS > limitS ? "over" : usedS >= limitS * warnAt ? "warn" : "ok";

const isWork = (s: Segment) => s.state !== "rest" && s.state !== "no_data";

// Jornada: del primer al último movimiento del día. "Sin datos" no la abre: un
// equipo apagado a medianoche no es trabajo.
export const workWindowS = (segments: Segment[]) => {
  const work = segments.filter(isWork);
  return work.length ? Math.max(0, (work[work.length - 1].end_ts - work[0].start_ts) / 1000) : 0;
};

// La jornada viene de la madrugada anterior si el día ABRE con actividad a las
// 00:00: los tramos se cortan en la medianoche, así que lo que sigue a esa hora
// es el mismo tramo del día anterior. No se inventa un inicio: sólo se avisa.
export const carriesOverMidnight = (segments: Segment[], tz: string) =>
  segments.length > 0 && isWork(segments[0]) && fmtHM(segments[0].start_ts, tz) === "00:00";

// Lunes de la semana de `date` (YYYY-MM-DD), sin pasar por la zona del navegador.
export const mondayOf = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return addDays(date, -((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7));
};

export type HosClock = {
  key: "drive" | "window" | "rest" | "week" | "cycle";
  label: string;
  usedS: number | null; // null: todavía no se sabe (consulta de varios días cargando o con error)
  limitS: number | null;
  remainingS: number | null; // sólo con límite
  pct: number; // 0..1: contra el límite o, sin límite, contra la referencia neutra
  level: HosLevel;
};

const H = 3600;
const clock = (key: HosClock["key"], label: string, usedS: number | null, refS: number, limitH: number | null, min = false): HosClock => {
  const limitS = limitH != null ? limitH * H : null;
  const used = usedS ?? 0;
  const level: HosLevel =
    usedS == null || limitS == null ? "calc" : min ? (used >= limitS ? "met" : "pending") : hosLevel(used, limitS);
  return { key, label, usedS, limitS, remainingS: limitS != null ? limitS - used : null, pct: Math.min(1, used / (limitS ?? refS)), level };
};

/**
 * `cycle`: días D-7..D de la unidad, sólo totales (null mientras carga).
 * Los tres del día se llenan contra 24 h; la semana contra 7 × 24 h y los 8
 * días contra 8 × 24 h. Con límite, contra el límite.
 */
export const hosClocks = (day: DailyUnitSummary, cycle: DailyUnitSummary[] | null, limits: HosLimits, tz: string, isToday: boolean) => {
  const driveS = (d: DailyUnitSummary) => (d.driving_s || 0) + (d.maneuver_s || 0);
  const mine = cycle?.filter((d) => d.device_id === day.device_id) ?? null;
  const from8 = addDays(day.date, -7);
  const monday = mondayOf(day.date);
  const sumFrom = (from: string) => (mine ? mine.filter((d) => d.date >= from && d.date <= day.date).reduce((a, d) => a + driveS(d), 0) : null);
  const when = isToday ? "hoy" : "del día";
  return {
    carriesOver: carriesOverMidnight(day.segments, tz),
    clocks: [
      clock("drive", `Manejo ${when}`, driveS(day), 24 * H, limits.driving_day_h),
      clock("window", "Jornada", workWindowS(day.segments), 24 * H, limits.duty_day_h),
      clock("rest", `Descanso ${when}`, day.rest_s || 0, 24 * H, limits.rest_min_h, true),
      clock("week", "Manejo esta semana (lun–dom)", sumFrom(monday), 7 * 24 * H, limits.driving_week_h),
      clock("cycle", "Manejo últimos 8 días", sumFrom(from8), 8 * 24 * H, null),
    ],
  };
};

// "7:05", como el centro de un reloj ELD.
export const fmtClock = (s: number) => {
  const min = Math.round(Math.max(0, s) / 60);
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
};

// Valor del campo del modal → límite: vacío = sin límite; fuera de (0, tope] = error.
export const HOS_LIMIT_MAX_H: Record<keyof HosLimits, number> = { driving_day_h: 24, duty_day_h: 24, rest_min_h: 24, driving_week_h: 168 };
export const parseLimitInput = (key: keyof HosLimits, text: string): { value: number | null; error: string | null } => {
  const t = text.trim().replace(",", ".");
  if (!t) return { value: null, error: null };
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return { value: null, error: "Debe ser un número mayor que 0" };
  if (n > HOS_LIMIT_MAX_H[key]) return { value: null, error: `Máximo ${HOS_LIMIT_MAX_H[key]} h` };
  return { value: n, error: null };
};

// Quién configura los límites: lo mismo que decide PUT /company en el backend
// (assertCanEditCompany): el admin reseller cualquier compañía, el admin de la
// compañía sólo la suya. Ids de rol de functions/src/utils/policyAccess.js.
export const ADMIN_ROLE_ID = "HiLYcf6ndou53xpXNJ02";
export const ADMIN_RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ";
export const canEditHosLimits = (roleId: string, ownCompanyId: string, companyId: string) =>
  roleId === ADMIN_RESELLER_ROLE_ID || (roleId === ADMIN_ROLE_ID && !!ownCompanyId && ownCompanyId === companyId);
